import React, { useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { getUserDisplayName, getUserEmail } from '../auth/auth-utils';
import { getSecureApiCredentials } from '../services/secureTokenService';
import { configService } from '../config/appConfig';

interface Offer {
  code: string;
  description: string;
  shortDescription: string;
  expirationDate: string;
  effectiveDate: string;
  campaignId: string;
  apr: string | null;
  customAttributes: {
    productType: string;
    riskBureauName: string;
    addrBureauName: string;
    [key: string]: string;
  };
  bureaus: Array<{
    code: string;
    tradeBaseDate: string;
    fcraAttributes: Record<string, string>;
    scores: Record<string, string>;
  }>;
}

const Home: React.FC = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [hasRequestedOffers, setHasRequestedOffers] = useState(false);
  const [mobileError, setMobileError] = useState<string | null>(null);

  // Function to detect if the request is from a mobile browser
  const isMobileBrowser = (): boolean => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for mobile devices
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;
    const isMobile = mobileRegex.test(userAgent.toLowerCase());
    
    // Additional check for specific mobile browsers
    const isAndroid = /android/i.test(userAgent);
    const isiOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    
    return isMobile || isAndroid || isiOS;
  };

  // Map each Vite mode to its corresponding JS bundle
  const bundleMap: Record<string, () => Promise<any>> = {
    dev:  () => import('../../offers-flow-dev-latest.min.js'),
    nlb:  () => import('../../offers-flow-nlb-latest.min.js'),
    uat:  () => import('../../offers-flow-uat-latest.min.js'),
    demo: () => import('../../offers-flow-demo-latest.min.js'),
    prod: () => import('../../offers-flow-prod-latest.min.js'),
  };

  // Fetch offers using OfferFlowBuilder with secure token management
  const fetchOffers = async (): Promise<Offer[]> => {
    try {
      const mode = import.meta.env.MODE as string;
      console.log('Current mode:', mode);
      // @ts-ignore - No type declaration for minified JS file
      const { OfferFlowBuilder } = await (bundleMap[mode] ?? bundleMap['demo'])();

      // Getting secure API tokens using the secure service
      const apiCredentials = await getSecureApiCredentials(oktaAuth, mode);
      let builder;
    // DEV Start

        // //Configured mobile number and device ip given by TY
        //  const deviceIp = "174.229.114.193"
        
        //Configured mobile number and device ip
          const deviceIp = "2600:1006:b036:2554:0:17:e3c7:6c01";
         
          console.log('configService.isDevelopment():', configService.isDevelopment());
          console.log('configService.getMODE():', configService.getMODE());
          //DEV
          if (apiCredentials && configService.getMODE() == 'dev' && !configService.isDevelopment()) {  
            console.log(" In DEV Builder")
            builder = new OfferFlowBuilder()
              .withDeviceIp(deviceIp)
              .withToken(apiCredentials.access_token) // Use access token for API calls
              .withCompanyId("788038")
              .withSubcode("1197098")
              .withAttributes("CC", "PL")
              .getoffers();
          } else if (apiCredentials && ( configService.getMODE() == 'demo' || configService.getMODE() == 'prod') && !configService.isDevelopment()){
            console.log(" In Demo Builder")
              builder = new OfferFlowBuilder()
                .withDeviceIpDetection()
                .withToken(apiCredentials.access_token) // Use access token for API calls
                .withCompanyId("788038")
                .withSubcode("1197098")
                .withAttributes("CC", "PL")
                .getoffers();
          } else if (apiCredentials?.access_token) {
            builder = new OfferFlowBuilder()
              .withDeviceIp(deviceIp)
              .withToken(apiCredentials.access_token)
              .withCompanyId("788038")
              .withSubcode("1197098")
              .withAttributes("CC", "PL")
              .getoffers();
          } else {
            throw new Error('No access token available. Obtain a token before requesting offers.');
          } 
      // DEV END
      const response = await builder.run();
      
      // Extract offers from the API response structure
      const offers = response?.offers || [];
      
      return offers;
    } catch (error) {
      console.error('Error fetching offers:', error);
      return [];
    }
  };

  const handleGetOffers = async () => {
    // Check if request is from mobile browser
   /* if (!isMobileBrowser()) {
      setMobileError('Discover offers is only available for mobile browsers. Please access this feature from your mobile device.');
      setHasRequestedOffers(true);
      return;
    } */

    // Clear any previous mobile error
    setMobileError(null);
    setIsLoadingOffers(true);
    setHasRequestedOffers(true);
    try {
      const fetchedOffers = await fetchOffers();
      setOffers(fetchedOffers);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setOffers([]);
    } finally {
      setIsLoadingOffers(false);
    }
  };

  const handleLogout = async () => {
    try {
      await oktaAuth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!authState || !authState.isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#6c757d'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '20px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 20px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              margin: '0',
              fontSize: '28px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: '700'
            }}>
              Welcome Home! 🏠
            </h1>
            <p style={{
              margin: '5px 0 0 0',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              Hello, {getUserDisplayName()} 👋
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => window.location.href = '/profile'}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(102, 126, 234, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
              }}
            >
              👤 Profile
            </button>
            
            <button
              onClick={handleLogout}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                color: '#dc3545',
                border: '2px solid #dc3545',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#dc3545';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#dc3545';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 24px'
      }}>
        {/* User Info Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          padding: '32px',
          borderRadius: '20px',
          marginBottom: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              👤
            </div>
            <div>
              <h2 style={{
                margin: '0',
                color: '#2d3748',
                fontSize: '24px',
                fontWeight: '600'
              }}>
                User Dashboard
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                color: '#718096',
                fontSize: '16px'
              }}>
                Manage your account and explore offers
              </p>
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              padding: '20px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>📧 Email</h4>
              <p style={{ margin: '0', fontSize: '14px', opacity: 0.9 }}>{getUserEmail()}</p>
            </div>
          </div>
        </div>

        {/* Offers Section */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          padding: '32px',
          borderRadius: '20px',
          marginBottom: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{
              margin: '0 0 8px 0',
              color: '#2d3748',
              fontSize: '28px',
              fontWeight: '600'
            }}>
              🎁 Exclusive Offers
            </h2>
            <p style={{
              margin: '0',
              color: '#718096',
              fontSize: '16px'
            }}>
              Discover personalized deals just for you
            </p>
          </div>

          <button
            onClick={handleGetOffers}
            disabled={isLoadingOffers}
            style={{
              padding: '16px 32px',
              background: isLoadingOffers 
                ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)' 
                : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              cursor: isLoadingOffers ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: isLoadingOffers 
                ? 'none' 
                : '0 4px 20px rgba(72, 187, 120, 0.3)',
              marginBottom: hasRequestedOffers ? '32px' : '0'
            }}
            onMouseOver={(e) => {
              if (!isLoadingOffers) {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(72, 187, 120, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoadingOffers) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(72, 187, 120, 0.3)';
              }
            }}
          >
            {isLoadingOffers ? '⏳ Loading...' : '🛍️ Discover Offers'}
          </button>

          {hasRequestedOffers && (
            <>
              {mobileError ? (
                <div style={{
                  padding: '40px',
                  color: '#e53e3e',
                  background: 'rgba(245, 101, 101, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(245, 101, 101, 0.3)'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    📱❌
                  </div>
                  <h3 style={{
                    color: '#c53030',
                    margin: '0 0 12px 0',
                    fontSize: '20px'
                  }}>
                    Mobile Browser Required
                  </h3>
                  <p style={{
                    margin: '0',
                    fontSize: '16px',
                    lineHeight: '1.5'
                  }}>
                    {mobileError}
                  </p>
                </div>
              ) : isLoadingOffers ? (
                <div style={{
                  padding: '40px',
                  color: '#718096'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #e2e8f0',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <p style={{ fontSize: '16px', margin: '0' }}>
                    Fetching your personalized offers...
                  </p>
                </div>
              ) : offers.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gap: '20px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  textAlign: 'left'
                }}>
                  {offers.map((offer) => (
                    <div
                      key={offer.code}
                      style={{
                        background: offer.customAttributes?.productType === 'CC' 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'  // Credit Card - Purple
                          : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Personal Loan - Blue
                        color: 'white',
                        padding: '24px',
                        borderRadius: '16px',
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '-50px',
                        right: '-50px',
                        width: '100px',
                        height: '100px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                        position: 'relative'
                      }}>
                        <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '600' }}>
                          {offer.shortDescription || offer.description}
                        </h3>
                        <span style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          padding: '6px 12px',
                          borderRadius: '15px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {offer.customAttributes?.productType === 'CC' ? '💳 Card' : '💰 Loan'}
                        </span>
                      </div>
                      <p style={{
                        margin: '0 0 16px 0',
                        opacity: 0.9,
                        lineHeight: '1.5',
                        fontSize: '14px'
                      }}>
                        {offer.description}
                      </p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12px',
                        opacity: 0.8,
                        marginBottom: '12px'
                      }}>
                        <span style={{
                          background: 'rgba(255, 255, 255, 0.15)',
                          padding: '4px 8px',
                          borderRadius: '8px'
                        }}>
                          📂 {offer.customAttributes?.productType === 'CC' ? 'Credit Card' : 'Personal Loan'}
                        </span>
                        <span>⏰ {new Date(offer.expirationDate).toLocaleDateString()}</span>
                      </div>
                      <div style={{
                        fontSize: '11px',
                        opacity: 0.7,
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>Code: {offer.code}</span>
                        <span>Bureau: {offer.customAttributes?.riskBureauName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '50px',
                  color: '#718096'
                }}>
                  <div style={{
                    fontSize: '64px',
                    marginBottom: '16px'
                  }}>
                    🎈
                  </div>
                  <h3 style={{
                    color: '#2d3748',
                    margin: '0 0 8px 0',
                    fontSize: '24px'
                  }}>
                    No offers right now
                  </h3>
                  <p style={{
                    margin: '0',
                    fontSize: '16px',
                    opacity: 0.8
                  }}>
                    Don't worry! New exciting deals are coming soon. Check back later! 🚀
                  </p>
                </div>
              )}
            </>
          )}
        </div>
       
      </main>

      <style jsx="true">{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Home;